import type { ReactNode } from 'react';
import { Forbidden403 } from '@bookora/ui';

import type { Permission } from '@bookora/shared';
import { useLocation } from 'react-router-dom';

import { useCanAny } from '@/permissions/use-can';

import { findSectionByPath } from './sections';

/**
 * Route-level affordance gate.
 *
 * A user who types the URL of a section they cannot reach gets a Persian «دسترسی
 * ندارید» instead of a screen whose every request fails with a toast. The
 * enforcement is still the server's — see `permissions/use-can.ts` — and every
 * endpoint behind the wrapped screen refuses independently.
 *
 * The 403 names the page that was refused and who can open it for them — a
 * bare «دسترسی ندارید» became a support call that said exactly that and
 * nothing more. Permissions are granted by the clinic owner, per role, in
 * «کارکنان و نقش‌ها».
 */
export function RequirePermission({
  anyOf,
  children,
}: {
  anyOf: readonly Permission[];
  children: ReactNode;
}) {
  const allowed = useCanAny(anyOf);
  const { pathname } = useLocation();
  if (!allowed) {
    const section = findSectionByPath(pathname);
    const place = section && section.path !== '/' ? `«${section.label}»` : 'این صفحه';
    return (
      <Forbidden403
        detail={`برای دیدن ${place} دسترسی جداگانه لازم است. از مالک کلینیک بخواید اون رو در بخش «کارکنان و نقش‌ها» به نقش شما بده.`}
      />
    );
  }
  return <>{children}</>;
}
